import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { AccessTime as ClockIcon } from '@mui/icons-material';
import { buildCreditExpiryWarnings } from '../utils/creditExpiryWarnings';

/**
 * Credits about to lapse, shown where the user already is.
 *
 * Rendered in two places — the top of the Dashboard and above the balance card
 * on Credits & Subscriptions — from one component, so the two can never drift.
 * The bell shows the same sentence from the same rule.
 *
 * ── WHY IT IS NOT AN <Alert severity="warning"> ─────────────────────────────
 * That would be the obvious choice and it renders wrong here: this app's theme
 * maps `warning.main` to #90E2E2, a pale cyan. A deadline rendered in pale cyan
 * reads as an informational aside, or as disabled. So it follows the amber
 * recipe this app uses for anything the user must act on, rather than the
 * theme default.
 *
 * ── WHY IT IS NOT DISMISSIBLE ───────────────────────────────────────────────
 * The dismiss control and the localStorage flag behind it were deliberately
 * removed. A warning the user can
 * hide is a warning that stops working the moment it matters, and unlike most
 * notices this one has a deadline attached — it disappears on its own when the
 * credits are spent or gone.
 *
 * One banner per expiring batch, soonest first. Two kinds expiring within a
 * fortnight of each other is rare but real, and collapsing them would hide
 * whichever the user could still act on.
 */

const SURFACE = '#FFF8E1';
const BORDER = '#F0DFA8';
const ICON = '#8A4B00';
const TITLE = '#5C3200';
const BODY = '#7A5210';

export default function CreditExpiryBanner({ lots, now, onViewCredits = null }) {
  const warnings = buildCreditExpiryWarnings(lots, now);

  // Nothing expiring. Returning null keeps the parent's flex `gap` from
  // reserving space for an empty element.
  if (warnings.length === 0) return null;

  return (
    <>
      {warnings.map((warning) => (
        <Box
          key={warning.key}
          role="status"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: '16px',
            px: 2.5,
            py: 1.75,
          }}
        >
          <ClockIcon sx={{ color: ICON, fontSize: 22, flexShrink: 0 }} />

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography
              sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: 14, color: TITLE }}
            >
              {/* The amount and the kind lead, because "which credits" is the
                  first thing the reader needs to know. */}
              {warning.credits.toLocaleString()} {warning.source} credits expire soon
            </Typography>
            <Typography
              sx={{ fontFamily: 'Inter, sans-serif', fontSize: 13, color: BODY }}
            >
              {warning.expiresLabel
                ? `Use them before ${warning.expiresLabel} — credits expiring soonest are used first.`
                : 'Credits expiring soonest are used first.'}
            </Typography>
          </Box>

          {/* Only where there is somewhere else to go. On the Credits page the
              balance is already on screen, so a button pointing at it would be
              a link to the current page. */}
          {onViewCredits && (
            <Button
              onClick={onViewCredits}
              sx={{
                background: 'linear-gradient(135deg, #A7391E 0%, #FF7A59 100%)',
                color: '#fff',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: 13,
                px: 2.5,
                py: 1,
                textTransform: 'none',
                flexShrink: 0,
                '&:hover': { opacity: 0.92 },
              }}
            >
              View credits
            </Button>
          )}
        </Box>
      ))}
    </>
  );
}
